import api from './api';

// **INTERFACES FOR FRONTEND**
export interface ArticleWorkflowFilters {
  status?: string[];
  supplierId?: string;
  hasActiveRequests?: boolean;
  hasPendingSubmissions?: boolean;
  isRequestedByAdmin?: boolean;
  hasUnreadFeedback?: boolean;
}

export interface CreateArticleRequestData {
  articleIds: string[];
  requestedFields: string[];
  message?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  deadline?: string; // ISO string
}

export interface SubmitArticleData {
  modifiedFields: string[];
  message?: string;
  requestId?: string;
}

export interface ReviewSubmissionData {
  action: 'approved' | 'rejected' | 'needs_correction';
  comment?: string;
  requestedChanges?: string[];
}

export interface WorkflowStats {
  total: number;
  requested: number;
  submitted: number;
  approved: number;
  rejected: number;
  needsCorrection: number;
  pendingReview: number;
  completion: number;
}

// **ADMIN API FUNCTIONS**
export const articleWorkflowApi = {
  
  // **ADMIN ENDPOINTS**
  
  /**
   * Admin erstellt Artikel-Anfrage
   */
  createArticleRequest: async (data: CreateArticleRequestData) => {
    const response = await api.post('/admin/articles/request', data);
    return response.data;
  },

  /**
   * Admin reviewed Artikel-Submission
   */
  reviewArticleSubmission: async (articleId: string, submissionId: string, data: ReviewSubmissionData) => {
    const response = await api.post(`/admin/articles/${articleId}/submissions/${submissionId}/review`, data);
    return response.data;
  },

  /**
   * Admin lädt Artikel für Dashboard
   */
  getArticlesForAdmin: async (filters: ArticleWorkflowFilters = {}) => {
    const params = new URLSearchParams();
    
    if (filters.status && filters.status.length > 0) {
      filters.status.forEach(status => params.append('status', status));
    }
    if (filters.supplierId) params.append('supplierId', filters.supplierId);
    if (filters.hasActiveRequests) params.append('hasActiveRequests', 'true');
    if (filters.hasPendingSubmissions) params.append('hasPendingSubmissions', 'true');

    const response = await api.get(`/admin/articles?${params.toString()}`);
    return response.data;
  },

  /**
   * Admin lädt Workflow-Statistiken
   */
  getWorkflowStats: async (): Promise<{ success: boolean; stats: WorkflowStats }> => {
    const response = await api.get('/admin/workflow-stats');
    return response.data;
  },

  /**
   * Admin updates article status directly (for simple status changes)
   */
  updateArticleStatus: async (articleId: string, status: string, comment?: string) => {
    const response = await api.patch(`/admin/articles/${articleId}/status`, { status, comment });
    return response.data;
  },

  // **SUPPLIER ENDPOINTS**

  /**
   * Lieferant lädt seine Artikel
   */
  getArticlesForSupplier: async (filters: ArticleWorkflowFilters = {}) => {
    const params = new URLSearchParams();
    
    if (filters.status && filters.status.length > 0) {
      filters.status.forEach(status => params.append('status', status));
    }
    if (filters.isRequestedByAdmin !== undefined) {
      params.append('isRequestedByAdmin', filters.isRequestedByAdmin.toString());
    }
    if (filters.hasUnreadFeedback !== undefined) {
      params.append('hasUnreadFeedback', filters.hasUnreadFeedback.toString());
    }

    const response = await api.get(`/supplier/articles?${params.toString()}`);
    return response.data;
  },

  /**
   * Lieferant reicht Artikel ein
   */
  submitArticleToAdmin: async (articleId: string, data: SubmitArticleData) => {
    const response = await api.post(`/supplier/articles/${articleId}/submit`, data);
    return response.data;
  },

  /**
   * Lieferant markiert Feedback als gelesen
   */
  markFeedbackAsRead: async (articleId: string) => {
    const response = await api.post(`/supplier/articles/${articleId}/mark-feedback-read`);
    return response.data;
  }
};

export default articleWorkflowApi;
